import React from "react";
import { Box, Typography, useTheme, Grid2, Skeleton } from "@mui/material";
import { useGetEngineeringStatusBreakdownQuery } from "store/slices/apis/engineeringDashboardSlice";
import { useParams } from "react-router-dom";
import HighchartsReact from "highcharts-react-official";
import Highcharts, { color } from "highcharts";
import { fontWeight, letterSpacing, lineHeight } from "@mui/system";

const colorPalette = [
    "#415385", // static
    "#C05222", // static
    "#FDCA41", // static
    "#3E756D", // static
    "#CE412D", // static
    "#57A773", // new greenish
    "#A76D60", // earthy tone
    "#6E44FF", // violet/indigo
    "#FF6B6B", // bright red-pink
    "#00A9A5"  // teal
];


const DocumentStatusBreakdown = () => {
    const theme = useTheme();
    const { projectId } = useParams();
    const { data, isLoading } = useGetEngineeringStatusBreakdownQuery({ projectId });
    if (isLoading) {
        return (
            <Box
                sx={{
                    padding: 2,
                    background: "#fff",
                    borderRadius: 2,
                    border: '1px solid #2525252E',
                    mb: 2,
                    height: "424px",
                    width: "100%",
                    minHeight: "100%",
                }}
            >
                <Skeleton variant="text" width={200} height={30} />
                <Skeleton variant="rectangular" height={300} />
            </Box>
        );
    }

    const chartSeriesData = data.map((item, index) => {
        const isSmall = item.count <= 20;

        return {
            y: item.count,
            color: colorPalette[index % colorPalette.length],
            dataLabels: {
                inside: !isSmall,
                align: isSmall ? "left" : "right",
                style: {
                    fontSize: "13px",
                    fontWeight: 600,
                    color: isSmall ? "#000" : "#fff",
                    textOutline: "none",
                }
            }
        };
    });


    const chartOptions = {
        chart: {
            type: "bar",
            height: 340,
            marginTop: 28,
            spacingTop: 0,
            events: {
                load: function () {
                    const chart = this;
                    chart.renderer.text("Document Count", 0, 18)  // (x, y)
                        .css({
                            color: "#000000",
                            fontSize: "14px",
                            fontWeight: 400,
                            lineHeight: "100%",
                            letterSpacing: "-0.4px"
                        })
                        .add();
                }
            },
        },
        title: { text: "" },
        xAxis: {
            categories: data.map((item) => item.status),
            lineWidth: 0,
            tickLength: 0,
            offset: 40,
            labels: {
                style: {
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: "100%",
                    letterSpacing: "-0.4px",
                    color: "#182A4E",
                },
            },
            opposite: false,
        },
        yAxis: {
            min: 0,
            tickLength: 5,
            tickWidth: 1,
            tickColor: "#999",
            gridLineWidth: 0,
            opposite: true,
            lineColor: "#7581954D",
            lineWidth: 1,
            labels: {
                enabled: true,
                style: {
                    fontSize: "12px",
                    color: "#414D55",
                },
                formatter: function () {
                    return this.value;
                },
            },
        },
        legend: { enabled: false },
        plotOptions: {
            series: {
                dataLabels: {
                    enabled: true,
                }
            }
        },
        series: [
            {
                name: "Documents",
                data: chartSeriesData,
            },
        ],
        credits: { enabled: false },
    };


    return (
        <Box
            sx={{
                padding: 2,
                background: "#fff",
                borderRadius: 2,
                border: '1px solid #2525252E',
                mb: 2,
                height: "500px",
                width: "100%",
                minHeight: "100%",
            }}
        >
            <Grid2 container sx={{ mb: "33px" }}>
                <Grid2 xs={12}>
                    <Typography
                        variant="body"
                        sx={{
                            fontWeight: 500,
                            fontSize: "18px",
                            lineHeight: "100%",
                            letterSpacing: "-0.4px",
                            color: `${theme?.customSettings?.themeColor}`,
                        }}
                    >
                        Document Status Breakdown
                    </Typography>
                    <Typography
                        sx={{
                            mt: "5px",
                            color: "#5052588A",
                            fontSize: "14px",
                            lineHeight: "100%",
                            letterSpacing: "-0.4px",
                        }}
                    >
                        Document lifecycle stages to ensure timely approvals and project alignment
                    </Typography>
                </Grid2>
            </Grid2>
            <Grid2 container>
                <Grid2 xs={12}>
                    <Box sx={{ width: "100%" }}>
                        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                    </Box>
                </Grid2>

            </Grid2>
        </Box>
    );
};

export default DocumentStatusBreakdown;
